// src/Pages/TravelManagement.jsx (Contenedor Refactorizado)

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/api.js';
import RouteForm from '../Components/RouteForm.js';
import FilterForm from '../Components/FilterForm.js';
import RouteList from '../Components/RouteList.js';
import { useToast } from '../Components/ui/Toast.jsx';
import { useConfirm } from '../Components/ui/ConfirmDialog.jsx';
import styles from './TravelManagement.module.css';

// Estado inicial para el formulario de CRUD
const INITIAL_ROUTE_FORM_STATE = {
  id: null,
  date: '',
  departing_time: '',
  arrival_time: '',
  estimated_duration: '',
  departure_terminal_id: '',
  arrival_terminal_id: '',
  distance: '',
  bus_id: '',
  price: '',
};

// Estado inicial para el formulario de Filtros
const INITIAL_FILTER_FORM_STATE = {
  searchDate: '',
  searchDeparture: '',
  searchArrival: ''
};

const TravelManagement = () => {
  const [routes, setRoutes] = useState([]);
  const [terminals, setTerminals] = useState([]);
  const [buses, setBuses] = useState([]);
  const [routeForm, setRouteForm] = useState(INITIAL_ROUTE_FORM_STATE);
  const [filterState, setFilterState] = useState(INITIAL_FILTER_FORM_STATE);
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // --- Data Fetching: Centralizado y optimizado ---
  
  // 1. Carga inicial de dependencias (Terminales y Buses)
  useEffect(() => {
    const fetchDependencies = async () => {
      try {
        const [terminalsRes, busesRes] = await Promise.all([
          api.get('/list_terminal'),
          api.get('/bus')
        ]);
        setTerminals(terminalsRes.data || []);
        setBuses(busesRes.data || []);
      } catch (err) {

      }
    };
    fetchDependencies();
  }, []);

  // 2. Carga/Búsqueda de Rutas
  const fetchRoutes = useCallback(async (filters = filterState) => {
    setIsLoading(true);
    try {
      const res = await api.get('/route');
      let allRoutes = res.data || [];

      // Filtrado en frontend
      if (filters.searchDate) {
        allRoutes = allRoutes.filter(route => {
          if (!route.Departure_date) return false;
          const routeDate = route.Departure_date.split('T')[0]; // YYYY-MM-DD
          return routeDate === filters.searchDate;
        });
      }
      if (filters.searchDeparture) {
        allRoutes = allRoutes.filter(route =>
          route.Origin.toLowerCase().includes(filters.searchDeparture.toLowerCase())
        );
      }
      if (filters.searchArrival) {
        allRoutes = allRoutes.filter(route =>
          route.Destination.toLowerCase().includes(filters.searchArrival.toLowerCase())
        );
      }

      setRoutes(allRoutes);
    } catch (err) {

    } finally {
      setIsLoading(false);
    }
  }, [filterState]);

  useEffect(() => {
    fetchRoutes(INITIAL_FILTER_FORM_STATE); // Carga inicial
  }, [fetchRoutes]);

  // --- Optimizaciones de Datos para Selects ---
  const terminalOptions = useMemo(() =>
    terminals.map(t => ({ value: String(t.id), label: t.name }))
  , [terminals]);

  const busOptions = useMemo(() =>
    buses.map(b => ({
      value: String(b.ID),
      label: `${b.Plate}${b.Number_seats ? ` (${b.Number_seats} asientos)` : ''}`
    }))
  , [buses]);

  // --- Funciones de Mutación (Actualización de Estado Local) ---

  // 💡 Actualiza el array 'routes' directamente sin re-fetch
  const handleUpdateRouteList = (updatedRoute) => {
    setRoutes(prevRoutes => {
      // 1. Edición
      if (updatedRoute.ID && prevRoutes.some(r => r.ID === updatedRoute.ID)) {
        return prevRoutes.map(route =>
          route.ID === updatedRoute.ID ? { ...route, ...updatedRoute } : route
        );
      }
      // 2. Creación
      return [updatedRoute, ...prevRoutes];
    });
  };

  const resetForm = useCallback(() => setRouteForm(INITIAL_ROUTE_FORM_STATE), []);

  const handleDelete = useCallback(async (id) => {
    const ok = await confirm('¿Desactivar esta ruta? Esta acción es reversible.', {
      title: 'Confirmar desactivación',
      confirmText: 'Desactivar',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/route/${id}`);

      // Actualización local inmediata
      setRoutes(prevRoutes => prevRoutes.filter(r => r.ID !== id));
      if (routeForm.id === id) resetForm();

      showToast('Ruta desactivada correctamente.', { type: 'success' });
    } catch (err) {
      showToast('Error desactivando ruta', { type: 'error' });
    }
  }, [routeForm.id, resetForm, confirm, showToast]);

  const handleEdit = useCallback((route) => {
    // Lógica para buscar los IDs de terminal por nombre
    const getTerminalIdByName = (name) => {
      const terminal = terminals.find(t => t.name.toLowerCase() === name?.toLowerCase());
      return terminal ? terminal.id.toString() : "";
    };

    const depTermId = getTerminalIdByName(route.Origin);
    const arrTermId = getTerminalIdByName(route.Destination);

    // Extraer fecha y horas de Departure_date y Arrival_date
    let date = '';
    let departing_time = '';
    let arrival_time = '';

    if (route.Departure_date) {
      const depDate = new Date(route.Departure_date);
      date = depDate.toISOString().split('T')[0]; // YYYY-MM-DD
      departing_time = depDate.toTimeString().slice(0, 5); // HH:MM
    }

    if (route.Arrival_date) {
      const arrDate = new Date(route.Arrival_date);
      arrival_time = arrDate.toTimeString().slice(0, 5); // HH:MM
    }

    setRouteForm({
      id: route.ID,
      date,
      departing_time,
      arrival_time,
      estimated_duration: route.Duration != null ? route.Duration.toString() : "",
      departure_terminal_id: depTermId,
      arrival_terminal_id: arrTermId,
      distance: route.Distance != null ? route.Distance.toString() : "",
      bus_id: route.ID_Bus != null ? route.ID_Bus.toString() : "",
      price: route.Price != null ? route.Price.toString() : "",
    });
  }, [terminals]);

  // --- Handlers de Filtros ---
  const handleFilterSubmit = useCallback((filters) => {
    setFilterState(filters);
    fetchRoutes(filters);
  }, [fetchRoutes]);

  const handleFilterClear = useCallback(() => {
    setFilterState(INITIAL_FILTER_FORM_STATE);
    fetchRoutes(INITIAL_FILTER_FORM_STATE);
  }, [fetchRoutes]);

  // --- Renderizado ---
  return (
    <div className={styles.container}>
      <h2>Gestión de Rutas</h2>

      {/* 1. Formulario de Creación/Edición */}
      <RouteForm
        form={routeForm}
        setForm={setRouteForm}
        resetForm={resetForm}
        terminalOptions={terminalOptions}
        busOptions={busOptions}
        onSuccess={handleUpdateRouteList}
      />
      
      {/* 2. Formulario de Búsqueda/Filtros */}
      <FilterForm
        filterForm={filterState}
        terminalOptions={terminalOptions}
        onSubmit={handleFilterSubmit}
        onClear={handleFilterClear}
      />

      <h3 className={styles.listTitle}>Lista de Rutas</h3>
      
      {isLoading && <div className={styles.loadingMessage}>Cargando rutas...</div>}

      {/* 3. Lista de Rutas */}
      {!isLoading && (
        <RouteList
          routes={routes}
          terminalOptions={terminalOptions}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
};

export default TravelManagement;